import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { Typography, Radio, notification } from 'antd';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../util/colors';
import { getToken } from '../../util/tokenCookies';
import { getSubscriptionDetails } from '../../services/api';
import { AuthContext } from '../../context/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { PageTitle } from '../../components/PageTitle';
import {canUpgrade, parsePlanId} from "./utils/silverplus.utils.ts";
import {FEATURES, MESSAGES, PlanId, PLANS, SubscriptionOption} from "./utils/silverplus.constants.ts";
import {usePaymentMutation, useSubscriptionState} from "./hooks/useSubscription.ts";
import {SubscriptionEndInfo} from "./components/SubscriptionEndInfo.tsx";
import {PricingCardButton} from "./components/PricingCardButton.tsx";

const { Title, Paragraph, Text } = Typography;

export const SilverPlusSubscription: React.FC = () => {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();

    const { data: subscriptionDetails } = useQuery({
        queryKey: ['subscriptionDetails'],
        queryFn: getSubscriptionDetails,
        enabled: !!user
    });

    const currentPlanId = parsePlanId(user?.plan_label);
    const [selectedOption, setSelectedOption] = useState<PlanId>(currentPlanId);

    const subscriptionState = useSubscriptionState(user??null, subscriptionDetails, currentPlanId);
    const paymentMutation = usePaymentMutation();

    const effectivePlanId = subscriptionState.type === 'treatAsFree' ? '0' : currentPlanId;

    useEffect(() => {
        setSelectedOption(effectivePlanId);
    }, [effectivePlanId]);

    const subscriptionOptions: SubscriptionOption[] = useMemo(() =>
        Object.values(PLANS).map(plan => ({
            ...plan,
            features: plan.id === '0' ? FEATURES["0"] : FEATURES.paid,
            popular: (subscriptionState.type === 'treatAsFree' || currentPlanId === '0') && plan.id === '6'
        })), [subscriptionState.type, currentPlanId]
    );

    const handlePayment = useCallback((planKey: string) => {
        const authToken = getToken();
        if (!authToken) {
            notification.error({
                message: MESSAGES.errors.authRequired,
                description: MESSAGES.errors.authDescription
            });
            return;
        }
        paymentMutation.mutate(planKey);
    }, [paymentMutation]);

    return (
        <Container>
            <PageTitle title="Schneller zum passenden Job mit silver+ für erfahrene Fachkräfte" />

            <SubscriptionEndInfo
                subscriptionState={subscriptionState}
                userPlanLabel={user?.plan_label}
                onManageClick={() => navigate('/account/silverplus-membership')}
            />

            <MainContent>
                <CenteredText>
                    <Paragraph style={{ fontSize: '18px', color: '#777', maxWidth: '600px', margin: '0 auto' }}>
                        Wählen Sie Ihre Option:
                    </Paragraph>
                </CenteredText>

                <PricingCardsContainer>
                    <Radio.Group
                        value={selectedOption}
                        onChange={(e) => setSelectedOption(e.target.value as PlanId)}
                        style={{ width: '100%' }}
                    >
                        <PricingCardsGrid>
                            {subscriptionOptions.map((option) => {
                                const isCurrentPlan = effectivePlanId === option.id;
                                const isUpgrade = canUpgrade(effectivePlanId, option.id);

                                return (
                                    <PricingCard
                                        key={option.id}
                                        $isPopular={option.popular}
                                        $isCurrentPlan={isCurrentPlan}
                                    >
                                        <Radio value={option.id} style={{ width: '100%' }}>
                                            <CardContent>
                                                <CardHeader>
                                                    <CardTitle>{option.months}</CardTitle>
                                                    {option.popular && <PopularBadge>Beliebt</PopularBadge>}
                                                </CardHeader>

                                                <CardPeriod>{option.period}</CardPeriod>
                                                <PriceContainer>
                                                    <CardPrice>
                                                        {option.price}
                                                        <span>{option.periodText}</span>
                                                    </CardPrice>
                                                    <CardOldPrice>
                                                        <span className="price-value">{option.discountPrice}</span>
                                                        {option.price !== "0 €" && <span id="old-price">{option.periodText}</span>}
                                                    </CardOldPrice>
                                                </PriceContainer>
                                                <PricingCardButton
                                                    option={option}
                                                    isCurrentPlan={isCurrentPlan}
                                                    isUpgrade={isUpgrade}
                                                    isPending={paymentMutation.isPending}
                                                    isLoading={paymentMutation.isPending && paymentMutation.variables === option.planKey}
                                                    userPlanEndDate={user?.plan_end_date}
                                                    currentPlanId={currentPlanId}
                                                    onClick={() => {
                                                        setSelectedOption(option.id);
                                                        handlePayment(option.planKey);
                                                    }}
                                                />

                                                <CardFeatures>
                                                    {option.features.map((feature, index) => (
                                                        <FeatureItem key={index}>
                                                            <FeatureCheck>✓</FeatureCheck>
                                                            <FeatureText>{feature}</FeatureText>
                                                        </FeatureItem>
                                                    ))}
                                                </CardFeatures>
                                            </CardContent>
                                        </Radio>
                                    </PricingCard>
                                );
                            })}
                        </PricingCardsGrid>
                    </Radio.Group>
                </PricingCardsContainer>
            </MainContent>
        </Container>
    );
};

// ============= STYLED COMPONENTS =============
const Container = styled.div`
  margin: 0 auto;
  padding: 20px;
  min-height: 100vh;
`;

const MainContent = styled.div`
  padding: 16px 0;
`;

const CenteredText = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const PricingCardsContainer = styled.div`
  margin: 0 auto;
`;

const PricingCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  justify-content: center;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PricingCard = styled.div<{
    $isPopular: boolean;
    $isCurrentPlan: boolean;
}>`
    border-radius: 16px;
    padding: 24px;
    background: white;
    position: relative;
    transition: all 0.3s ease;
    cursor: pointer;
    min-height: 400px;
    width: 100%;
    display: flex;
    flex-direction: column;

    ${props => props.$isPopular ? `
    border: 2px solid transparent;
    background-image: linear-gradient(white, white), 
                      linear-gradient(135deg, #CA6673 0%, #9177C7 50%, #4796E3 100%);
    background-origin: border-box;
    background-clip: padding-box, border-box;
  ` : `
    border: 2px solid #e2e8f0;
  `}

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    }

    .ant-radio-wrapper {
        width: 100%;
        margin: 0;
        height: 100%;
    }

    .ant-radio {
        display: none;
    }
`;

const CardContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
`;

const CardTitle = styled(Title)`
  font-size: 1.25rem !important;
  font-weight: 600 !important;
  color: ${colors.text} !important;
  margin: 0 !important;
`;

const PopularBadge = styled.span`
    background: linear-gradient(135deg, #CA6673 0%, #9177C7 50%, #4796E3 100%);
    color: white;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.875rem;
    font-weight: 600;
    position: absolute;
    right: 0;
    top: -40px;
`;

const CardPeriod = styled(Text)`
  color: #777 !important;
  font-size: 0.875rem !important;
  margin: 0 !important;
  min-height: 32px;
`;

const PriceContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
`

const CardOldPrice = styled(Title)`
    font-size: 1.5rem !important;
    margin: 0 !important;
    color: ${colors.text} !important;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 4px;

    .price-value {
        text-decoration: line-through;
    }

    span#old-price {
        font-size: 0.8rem;
        color: #777;
        font-weight: 500;
    }
`;

const CardPrice = styled(Title)`
    margin: 0 !important;
  font-size: 1.5rem !important;
  color: ${colors.pink} !important;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 4px;

  span {
    font-size: 0.8rem;
    color: #777;
    font-weight: 500;
  }
`;

const CardFeatures = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const FeatureCheck = styled.span`
  color: #00A9B2;
  font-weight: bold;
  font-size: 1rem;
  flex-shrink: 0;
  margin-top: 2px;
`;

const FeatureText = styled(Text)`
  font-size: 0.875rem !important;
  line-height: 1.4 !important;
  color: ${colors.text} !important;
  margin: 0 !important;
`;
